import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { streamingServiceApi } from "@/services/streaming-service";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface StreamingServiceComboboxProps {
  value: string; // service id as string, or "all"
  onChange: (value: string) => void;
  disabled?: boolean;
}

export function StreamingServiceCombobox({
  value,
  onChange,
  disabled,
}: StreamingServiceComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const { data: servicesResponse, isLoading } = useQuery({
    queryKey: ["streaming-services", "search", debouncedSearch],
    queryFn: () =>
      streamingServiceApi.getAll({
        name: debouncedSearch || undefined,
        page: 1,
        limit: 20,
      }),
    enabled: open,
  });

  const services = servicesResponse?.data || [];
  const selectedService = services.find((s) => String(s.id) === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-44 justify-between font-normal"
          disabled={disabled}
        >
          {value !== "all" && selectedService ? (
            <span className="flex items-center gap-2 truncate">
              <span>{selectedService.icon}</span>
              <span className="truncate">{selectedService.name}</span>
            </span>
          ) : (
            <span className="text-muted-foreground">All Services</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-52 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Buscar servicio..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {isLoading || search !== debouncedSearch ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            ) : (
              <CommandGroup>
                <CommandItem
                  value="all"
                  onSelect={() => {
                    onChange("all");
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === "all" ? "opacity-100" : "opacity-0"
                    )}
                  />
                  All Services
                </CommandItem>
                {services.length === 0 ? (
                  <CommandEmpty>No se encontraron servicios</CommandEmpty>
                ) : (
                  services.map((service) => (
                    <CommandItem
                      key={service.id}
                      value={String(service.id)}
                      onSelect={() => {
                        onChange(String(service.id));
                        setOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          value === String(service.id) ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <span className="flex items-center gap-2">
                        <span>{service.icon}</span>
                        <span>{service.name}</span>
                      </span>
                    </CommandItem>
                  ))
                )}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
